import React, { useContext, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { FlexMiddle, ContextMenu, CircleIcon } from '@ubeya/shared-web/components';
import { ReactComponent as PlusIcon } from '@ubeya/shared-web/assets/plus.svg';
import { convertTimeStringToMoment } from '@ubeya/shared/utils/time';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { showAllBranchesMode } from 'shared/atoms/scheduling';
import BaseRenderer from './BaseRenderer';

const SectionRenderer = ({
  isGroup,
  label,
  isExpended,
  toggleRow,
  showLabelBranches = false,
  onPublish,
  onEdit,
  onDelete,
  onCopyShifts,
  onCreateShift,
  showEditOptions = true
}) => {
  const { t } = useContext(ConfigContext);
  const { timeFormat } = useContext(ConfigContext);
  const { mappedBranches } = useBranches();

  const value = useMemo(() => {
    const startTime = convertTimeStringToMoment(label.startTime).format(timeFormat);
    const endTime = convertTimeStringToMoment(label.endTime).format(timeFormat);

    if (label.startTime && label.endTime) {
      return (
        <>
          <span>{label.title} </span>
          {showLabelBranches && label.branches && (
            <span> ({label.branches.map((branch) => mappedBranches[branch].name)}) </span>
          )}
          <span dir="rtl">
            <span>{startTime}</span>
            <span> - </span>
            <span>{endTime}</span>
          </span>
        </>
      );
    }
    return (
      <>
        <span>{label.title} </span>
        {showLabelBranches && label.branches && (
          <span> ({label.branches.map((branch) => mappedBranches[branch].name)}) </span>
        )}
      </>
    );
  }, [label.branches, label.endTime, label.startTime, label.title, mappedBranches, showLabelBranches, timeFormat]);

  const options = useMemo(
    () => [
      { label: t('publish'), onClick: onPublish, visible: !!onPublish },
      { label: t('edit'), onClick: onEdit },
      { label: t('copy'), onClick: onCopyShifts, visible: !!onCopyShifts },
      { label: t('delete'), onClick: onDelete, visible: false } // TODO: visible when implemented in BE
    ],
    [onCopyShifts, onDelete, onEdit, onPublish, t]
  );

  const showAllBranches = useRecoilValue(showAllBranchesMode);

  return (
    <BaseRenderer
      {...{
        isGroup,
        value,
        isExpended,
        toggleRow,
        color: label?.color
      }}>
      {showEditOptions && !showAllBranches && (
        <FlexMiddle className="extra">
          {onCreateShift && (
            <CircleIcon
              className="editable"
              icon={PlusIcon}
              onClick={(e) => {
                e.stopPropagation();
                onCreateShift();
              }}
            />
          )}
          <ContextMenu options={options} usePortal className="editable" />
        </FlexMiddle>
      )}
    </BaseRenderer>
  );
};

export default SectionRenderer;
